import React, { useState, useEffect, useRef } from "react";
import { Input, Button } from "antd";
import { EditFilled, CheckOutlined, CloseOutlined } from "@ant-design/icons";

const InlineEditableNameCell = ({
  value,
  record,
  placeholder,
  editing,
  onStartEdit,
  onSave,
  onCancel,
  loading,
}) => {
  const [localValue, setLocalValue] = useState(value || "");
  const [originalValue, setOriginalValue] = useState(value || "");
  const inputRef = useRef(null);
  const cancelNextBlurRef = useRef(false);

  useEffect(() => {
    if (!editing) {
      setLocalValue(value || "");
      setOriginalValue(value || "");
    }
  }, [value, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editing]);

  const handleSave = () => {
    const trimmed = (localValue || "").trim();
    console.log("InlineEditableNameCell save", {
      id: record?.id,
      value: trimmed,
      record,
    });

    // If value didn't change, just exit edit mode
    if (trimmed === (originalValue || "").trim()) {
      if (onCancel) {
        onCancel(record);
      }
      return;
    }

    if (onSave) {
      onSave(trimmed, record);
    }
  };

  const handleBlur = () => {
    if (cancelNextBlurRef.current) {
      cancelNextBlurRef.current = false;
      return;
    }
    handleSave();
  };

  const handleKeyDown = event => {
    if (event.key === "Enter") {
      // Prevent subsequent blur from triggering another save
      cancelNextBlurRef.current = true;
      handleSave();
    } else if (event.key === "Escape") {
      cancelNextBlurRef.current = true;
      setLocalValue(originalValue || "");
      if (onCancel) {
        onCancel(record);
      }
    }
  };

  return (
    <div className="flex items-center group">
      {editing ? (
        <div className="flex items-center w-full">
          <Input
            ref={inputRef}
            value={localValue}
            placeholder={placeholder}
            onChange={e => setLocalValue(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            className="mr-2 flex-1"
          />
          <Button
            type="primary"
            icon={<CheckOutlined />}
            onMouseDown={() => {
              // Avoid blur-triggered save running in addition to this click
              cancelNextBlurRef.current = true;
            }}
            loading={loading}
            onClick={handleSave}
          />
          <Button
            className="ml-1"
            icon={<CloseOutlined />}
            onMouseDown={() => {
              // Avoid blur-triggered save; we'll cancel instead
              cancelNextBlurRef.current = true;
            }}
            onClick={() => {
              setLocalValue(originalValue || "");
              if (onCancel) {
                onCancel(record);
              }
            }}
          />
        </div>
      ) : (
        <div className="flex items-center justify-between w-full">
          <span className="truncate max-w-full">{value && value.trim() ? value : "--"}</span>
          <Button
            type="text"
            size="small"
            className="ml-1 opacity-0 group-hover:opacity-100"
            icon={<EditFilled className="text-[#00000073]" />}
            onClick={() => {
              if (onStartEdit) {
                onStartEdit(record);
              }
            }}
          />
        </div>
      )}
    </div>
  );
};

export default InlineEditableNameCell;
